<template>
  <div v-if="value" :class="cls.value" :style="rootStyle">
    <span v-if="value.prefix" :class="cls.valuePrefix" :style="prefixStyle">
      {{ value.prefix }}
    </span>

    <ScrollingNumber
      v-if="shouldAnimate"
     :text="String(displayContent ?? '')"
      :class="cls.valueContent"
      :style="contentFontStyle"
    />
    <span v-else :class="cls.valueContent" :style="contentFontStyle">
      {{ displayContent }}
    </span>

    <span v-if="value.suffix" :class="cls.valueSuffix" :style="suffixStyle">
      {{ value.suffix }}
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, type CSSProperties, type PropType } from 'vue'
import { useNamespace } from '@jdesign-vue/hooks'
import type { StatisticCardValue as StatisticCardValueModel, StatisticCardSize, StatisticVNode } from '../types'
import { createColorVars, STATISTIC_CARD_CLASSES, VALUE_FONT_SIZE } from '../constants'
import { loadJDZhengHTRegular } from '../font-loader'
import ScrollingNumber from './scrolling-number.vue'

// 模块级单例：整个应用只在首次渲染指标卡数值时加载一次在线字体
let jdZhengHTLoaded = false
function ensureJDZhengHTFont(): void {
  if (jdZhengHTLoaded) return
  jdZhengHTLoaded = true
  loadJDZhengHTRegular()
}

function parseNumericValue(value: StatisticVNode): number | null {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return parseFloat(trimmed)
  }
  return null
}

const SIZE_CONFIG = {
  large: {
    prefix: { fontSize: '24px', lineHeight: '36px' },
    suffix: { fontSize: '14px', lineHeight: '14px' },
    content: { lineHeight: '36px' },
  },
  small: {
    prefix: { fontSize: '20px', lineHeight: '32px' },
    suffix: { fontSize: '12px', lineHeight: '12px' },
    content: { lineHeight: '32px' },
  },
} as const

export default defineComponent({
  name: 'StatisticCardValue',
  components: { ScrollingNumber },
  props: {
    value: { type: Object as PropType<StatisticCardValueModel>, default: undefined },
    size: { type: String as PropType<StatisticCardSize>, required: true },
    numberScroll: { type: Boolean, default: false },
  },
  setup(props) {
    const cls = STATISTIC_CARD_CLASSES
    const ns = useNamespace('statistic-card')
    const colorVars = createColorVars(ns.cssVarName)

    // 组件挂载时自动加载京东正黑体（模块级单例，只加载一次）
    onMounted(() => {
      ensureJDZhengHTFont()
    })

    const rawContent = computed(() => props.value?.content ?? props.value?.emptyText ?? '—')
    const parsedNumeric = computed(() => parseNumericValue(rawContent.value))
    const shouldAnimate = computed(() => props.numberScroll && parsedNumeric.value !== null)

    const displayContent = computed(() => {
      if (props.value?.formatter) {
        return props.value.formatter(rawContent.value)
      }
      return rawContent.value
    })

    const config = computed(() => SIZE_CONFIG[props.size])

    const rootStyle = computed<CSSProperties>(() => ({
      display: 'flex',
      alignItems: 'baseline',
      gap: '2px',
      color: colorVars.textPrimary,
      fontSize: `${VALUE_FONT_SIZE[props.size]}px`,
      lineHeight: config.value.content.lineHeight,
    }))

    const contentFontStyle: CSSProperties = {
      fontFamily: 'JDZhengHT-EN',
      fontWeight: 400,
    }

    const prefixStyle = computed<CSSProperties>(() => ({
      fontFamily: 'JDZhengHT-EN',
      fontWeight: 400,
      ...config.value.prefix,
    }))

    const suffixStyle = computed<CSSProperties>(() => ({
      fontFamily: 'inherit',
      fontWeight: 400,
      ...config.value.suffix,
    }))

    return {
      cls,
      shouldAnimate,
      displayContent,
      rootStyle,
      contentFontStyle,
      prefixStyle,
      suffixStyle,
    }
  },
})
</script>